import { useEffect, useRef } from 'react'

type Refetch = () => Promise<unknown>

/** 一批相邻 durable 事件的合并窗口；50ms 不会形成可感知等待。 */
export const STREAM_PROJECTION_BATCH_MS = 50

/** 失败投影的首次重试间隔；之后指数退避，但不会无限拉长。 */
export const STREAM_PROJECTION_RETRY_BASE_MS = 100

/** 只限制重试间隔，不限制自愈次数；成功或切换会话后立即停止。 */
export const STREAM_PROJECTION_RETRY_MAX_MS = 2_000

export interface ConversationProjectionRefetchers {
  readonly messages: Refetch
  readonly conversation: Refetch
  readonly workboard: Refetch
  // 2026-09-08 审计加的三份。原来只刷上面三份，于是：
  //   participants  别人被拉进会话，你的团队栏不动
  //   decisions     红级审批到了，「要你决定」不亮——而那正是这个产品的核心动作
  //   plan          Lulu 拆好计划，卡不出现
  // 三份都靠各自的轮询或者手动刷新才跟上。
  readonly participants: Refetch
  readonly decisions: Refetch
  readonly plan: Refetch
}

/**
 * 同一条 durable stream revision 到达时，三份会影响终态的投影一起刷新。
 *
 * 用 allSettled 而不是串行 await：消息接口暂时失败时，会话终态与工作板仍要
 * 立即更新；三次请求也会在同一轮启动，不平白增加一段等待。
 */
export function refreshConversationProjection(
  refetchers: ConversationProjectionRefetchers,
): Promise<PromiseSettledResult<unknown>[]> {
  return Promise.allSettled([
    refetchers.messages(),
    refetchers.conversation(),
    refetchers.workboard(),
    refetchers.participants(),
    refetchers.decisions(),
    refetchers.plan(),
  ])
}

interface StreamProjectionRefreshInput extends ConversationProjectionRefetchers {
  readonly conversationId: string | undefined
  readonly revision: number
}

const PROJECTION_KEYS = [
  'messages',
  'conversation',
  'workboard',
  'participants',
  'decisions',
  'plan',
] as const
type ProjectionKey = (typeof PROJECTION_KEYS)[number]

interface ProjectionRefreshRuntime {
  readonly observe: (revision: number) => void
  readonly dispose: () => void
}

/**
 * 一条会话自己的刷新调度器。
 *
 * - 首个 revision 锁定 50ms 窗口，后续事件只抬高目标 revision，不重置窗口；
 * - 三份投影并发刷新，成功项记账，失败项按有上限的短退避自动重试；
 * - in-flight 时到达的新 revision 会在本轮结束后再跑一轮；
 * - dispose 后旧会话的计时器与异步回调都不能再续跑。
 *
 * 这不是轮询：没有待兑现的 durable revision 时没有计时器，也没有请求。
 */
function createProjectionRefreshRuntime(
  getRefetchers: () => ConversationProjectionRefetchers,
): ProjectionRefreshRuntime {
  const fulfilledRevision: Record<ProjectionKey, number> = {
    messages: 0,
    conversation: 0,
    workboard: 0,
    participants: 0,
    decisions: 0,
    plan: 0,
  }
  let targetRevision = 0
  let retryAttempt = 0
  let inFlight = false
  let disposed = false
  let timer: number | undefined
  let timerDueAt = 0

  const clearTimer = () => {
    if (timer !== undefined) window.clearTimeout(timer)
    timer = undefined
    timerDueAt = 0
  }

  const retryDelay = () =>
    Math.min(
      STREAM_PROJECTION_RETRY_BASE_MS * 2 ** Math.max(0, retryAttempt - 1),
      STREAM_PROJECTION_RETRY_MAX_MS,
    )

  function schedule(delay: number) {
    if (disposed || inFlight) return
    const dueAt = Date.now() + delay
    // 已经有更早的批次时保留它。连续事件不会把首个 50ms 窗口往后拖。
    if (timer !== undefined && timerDueAt <= dueAt) return
    clearTimer()
    timerDueAt = dueAt
    timer = window.setTimeout(() => {
      timer = undefined
      timerDueAt = 0
      void flush()
    }, delay)
  }

  async function flush() {
    if (disposed || inFlight || targetRevision === 0) return
    const attemptRevision = targetRevision
    const keys = PROJECTION_KEYS.filter(
      (key) => fulfilledRevision[key] < attemptRevision,
    )
    if (keys.length === 0) return

    inFlight = true
    const refetchers = getRefetchers()
    const settled = await Promise.allSettled(
      keys.map((key) => Promise.resolve().then(() => refetchers[key]())),
    )
    inFlight = false
    if (disposed) return

    settled.forEach((result, index) => {
      const key = keys[index]
      if (result.status === 'fulfilled' && key !== undefined) {
        fulfilledRevision[key] = Math.max(fulfilledRevision[key], attemptRevision)
      }
    })

    if (targetRevision > attemptRevision) {
      // 本轮请求期间又来了 durable event：立即兑现更新后的目标，不能丢终态。
      retryAttempt = 0
      schedule(0)
      return
    }

    const hasFailures = PROJECTION_KEYS.some(
      (key) => fulfilledRevision[key] < targetRevision,
    )
    if (hasFailures) {
      retryAttempt += 1
      schedule(retryDelay())
    } else {
      retryAttempt = 0
    }
  }

  return {
    observe(revision) {
      if (disposed || !Number.isFinite(revision) || revision <= targetRevision) return
      targetRevision = revision
      retryAttempt = 0
      // in-flight 会由 flush 尾部续一轮；其余情况由首事件启动固定批次窗口。
      if (!inFlight) schedule(STREAM_PROJECTION_BATCH_MS)
    },
    dispose() {
      disposed = true
      clearTimer()
    },
  }
}

/**
 * 把 SSE 的 durable revision 接到三份查询。
 *
 * 同一 revision 因组件重渲染再次进入时不重复刷新；SSE 自身重放同一条消息时，
 * 消息缓存仍按 sequence 幂等追加，因而不会制造第二张结果卡。
 */
export function useStreamProjectionRefresh({
  conversationId,
  revision,
  messages,
  conversation,
  workboard,
  participants,
  decisions,
  plan,
}: StreamProjectionRefreshInput): void {
  const latestRefetchers = useRef<ConversationProjectionRefetchers>({
    messages,
    conversation,
    workboard,
    participants,
    decisions,
    plan,
  })
  const runtime = useRef<ProjectionRefreshRuntime | null>(null)
  latestRefetchers.current = { messages, conversation, workboard, participants, decisions, plan }

  useEffect(() => {
    if (conversationId === undefined) {
      runtime.current = null
      return
    }

    const current = createProjectionRefreshRuntime(() => latestRefetchers.current)
    runtime.current = current
    if (revision > 0) current.observe(revision)

    return () => {
      current.dispose()
      if (runtime.current === current) runtime.current = null
    }
    // revision 由下面的轻量 effect 喂给同一个会话调度器；不能因每条事件重建它。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  useEffect(() => {
    if (conversationId === undefined || revision === 0) return
    runtime.current?.observe(revision)
  }, [conversationId, revision])
}
